
import React, { useState } from 'react';
import { getPluginDetails } from '../services/geminiService'; // Adjusted path
import { PluginInfo } from '../types'; // Adjusted path

interface PluginListCardProps {
  title: string;
  plugins: string[];
  icon?: React.ReactNode;
  bgColorClass?: string;
  textColorClass?: string;
  emptyMessage?: string;
}

const PluginListCard: React.FC<PluginListCardProps> = ({ 
  title, 
  plugins, 
  icon, 
  bgColorClass = 'bg-slate-700', 
  textColorClass = 'text-slate-100',
  emptyMessage = "No plugins in this list."
}) => {
  const [selectedPluginForInfo, setSelectedPluginForInfo] = useState<string | null>(null);
  const [pluginInfo, setPluginInfo] = useState<PluginInfo | null>(null);
  const [isLoadingInfo, setIsLoadingInfo] = useState<boolean>(false);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);

  const handleShowInfo = async (pluginName: string) => {
    setSelectedPluginForInfo(pluginName);
    setIsLoadingInfo(true);
    setPluginInfo(null);
    setInfoError(null);
    setIsInfoModalOpen(true); // Open modal to show loading state

    try {
      const details = await getPluginDetails(pluginName);
      setPluginInfo(details);
    } catch (err) {
      if (err instanceof Error) {
        setInfoError(err.message);
      } else {
        setInfoError("An unknown error occurred while fetching plugin details.");
      }
    } finally {
      setIsLoadingInfo(false);
    }
  };

  const formatKeyName = (key: string): string => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <>
      <div className={`rounded-lg shadow-xl p-6 ${bgColorClass} ${textColorClass} min-h-[200px] flex flex-col`}>
        <h3 className="text-2xl font-semibold mb-4 flex items-center">
          {icon && <span className="mr-3 text-3xl">{icon}</span>}
          {title} ({plugins.length})
        </h3>
        {plugins.length > 0 ? (
          <ul className="space-y-2 text-sm overflow-y-auto flex-grow max-h-96 pr-2">
            {plugins.map((plugin, index) => (
              <li
                key={index}
                className="p-2 bg-slate-800 rounded hover:bg-slate-700 transition-colors duration-150 flex justify-between items-center group"
              >
                <span className="truncate max-w-[calc(100%-4rem)]" title={plugin}>{plugin}</span>
                <button
                  onClick={() => handleShowInfo(plugin)}
                  disabled={isLoadingInfo && selectedPluginForInfo === plugin}
                  className="ml-2 px-2 py-1 text-xs bg-sky-600 hover:bg-sky-500 text-white rounded-md disabled:opacity-50 disabled:cursor-wait opacity-0 group-hover:opacity-100 transition-opacity"
                  title={`Get info for ${plugin}`}
                >
                  {isLoadingInfo && selectedPluginForInfo === plugin ? '...' : 'Info'}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-slate-400 italic">{emptyMessage}</p>
        )}
      </div>

      {isInfoModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 transition-opacity duration-300 ease-in-out">
          <div className="bg-slate-800 p-6 rounded-lg shadow-xl max-w-lg w-full text-slate-100 transform transition-all duration-300 ease-in-out scale-95 opacity-0 animate-modalFadeInScaleUp">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-xl font-semibold text-sky-300 truncate">
                {pluginInfo?.name ? `Info: ${pluginInfo.name}` : (selectedPluginForInfo ? `Info: ${selectedPluginForInfo}`: "Plugin Information")}
              </h4>
              <button
                onClick={() => setIsInfoModalOpen(false)}
                className="text-slate-400 hover:text-slate-200 text-2xl"
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>

            {isLoadingInfo && (
              <div className="flex flex-col items-center justify-center h-32">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-500"></div>
                <p className="mt-3 text-slate-300">Fetching details for {selectedPluginForInfo}...</p>
              </div>
            )}
            {infoError && !isLoadingInfo && (
              <div className="bg-red-800 border border-red-600 p-3 rounded-md">
                <p className="text-red-100 font-semibold">Error:</p>
                <p className="text-red-200 text-sm">{infoError}</p>
              </div>
            )}
            {pluginInfo && !isLoadingInfo && !infoError && (
              <div className="space-y-2 text-sm max-h-96 overflow-y-auto pr-2">
                {Object.entries(pluginInfo).map(([key, value]) => {
                  if (value && typeof value === 'string' && value.trim() !== '') {
                    return (
                      <div key={key} className="border-b border-slate-700 py-2">
                        <strong className="text-sky-400 block mb-1">{formatKeyName(key)}:</strong>
                        <span className="text-slate-300 whitespace-pre-wrap break-words">{String(value)}</span>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            )}
             {!isLoadingInfo && (
                <button
                    onClick={() => setIsInfoModalOpen(false)}
                    className="mt-6 w-full px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                    Close
                </button>
             )}
          </div>
        </div>
      )}
      <style>{`
        @keyframes modalFadeInScaleUp {
          0% {
            opacity: 0;
            transform: scale(0.95);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-modalFadeInScaleUp {
          animation: modalFadeInScaleUp 0.3s forwards;
        }
      `}</style>
    </>
  );
};

export default PluginListCard;
